import React, {Suspense, useEffect, useMemo} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useSearchParams} from 'react-router-dom';
import {emitPageView} from '@di-shared/features/analytics';
import Header from '@di-shared/features/common/Header';
import HeaderCloseButton from '@di-shared/features/common/HeaderCloseButton';
import Tab from '@di-shared/features/common/Tab';
import {useInferableTranslation} from '@di-shared/features/common/useInferableTranslation';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import HomeTitleHeader from '@di-shared/features/home/HomeTitleHeader';
import BenefitContentsNa from '@di-shared/features/home/na/benefit/BenefitContents.na';
import DrivingContentsNa from '@di-shared/features/home/na/driving/DrivingContents.na';
import HomeEvBatteryNa from '@di-shared/features/home/na/ev-battery/HomeEvBattery.na';
import HomeTireNa from '@di-shared/features/home/na/tire/HomeTire.na';
import useAvailableComponentNa from '@di-shared/features/home/na/useAvailableComponent.na';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';

export interface HomeContentsNaProps {
  vin: string;
}

const TAB_VALUES = {
  DRIVING: 'driving',
  VEHICLE: 'vehicle',
  BENEFIT: 'benefit',
} as const;

type TabValue = (typeof TAB_VALUES)[keyof typeof TAB_VALUES];

function HomeContentsNa({vin}: HomeContentsNaProps) {
  const TAB_PAGE_ID = useMemo<Record<TabValue, string>>(
    () => ({
      [TAB_VALUES.DRIVING]: ROUTE_PATHS.HOME_DRIVING_TAB.id,
      [TAB_VALUES.VEHICLE]: ROUTE_PATHS.HOME_VEHICLE_TAB.id,
      [TAB_VALUES.BENEFIT]: ROUTE_PATHS.HOME_BENEFIT_TAB.id,
    }),
    []
  );

  const {t} = useInferableTranslation();
  const {resolveFeatureFlag} = useFirebaseStore(({resolveFeatureFlag}) => ({resolveFeatureFlag}));
  const {checkAvailableComponent, isUbiAvailable} = useAvailableComponentNa({vin});
  const isTireAvailable = checkAvailableComponent('TIRE_PRESSURE');
  const isEvBatteryAvailable = checkAvailableComponent('EV_BATTERY');
  const isTireVisible = resolveFeatureFlag('NA_TIRE_PRESSURE', vin, {serverSpecVisible: isTireAvailable});
  const isEvBatteryVisible = resolveFeatureFlag('NA_EV_BATTERY', vin, {serverSpecVisible: isEvBatteryAvailable});
  const isBenefitVisible = resolveFeatureFlag('NA_UBI', vin, {serverSpecVisible: isUbiAvailable});

  const [searchParams, setSearchParams] = useSearchParams();

  const selectedTab = useMemo(() => {
    return (searchParams.get('tab') as TabValue) || TAB_VALUES.DRIVING;
  }, [searchParams]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const pageId = TAB_PAGE_ID[selectedTab];
    if (pageId) {
      emitPageView(pageId);
    }
  }, [selectedTab]);

  function selectTab(tabValue: TabValue) {
    searchParams.set('tab', tabValue);
    setSearchParams(searchParams, {replace: true});
  }

  return (
    <div className="wrap">
      <Header
        className="header header_absolute"
        right={<HeaderCloseButton onClick={() => ccWebviewUtils.closeContent()} />}
      />

      <main
        id="content"
        className="content page_home"
      >
        <ErrorBoundary fallback={<HomeTitleHeader.Error />}>
          <Suspense fallback={<HomeTitleHeader.Loading />}>
            <HomeTitleHeader vin={vin} />
          </Suspense>
        </ErrorBoundary>
        {isTireVisible || isEvBatteryVisible || isBenefitVisible ? (
          <>
            <Tab
              element="div"
              className="tabbar_txt tabbar_sticky"
              role="tablist"
              selectedValue={selectedTab}
            >
              <div className="inner">
                <Tab.Item
                  element="button"
                  value={TAB_VALUES.DRIVING}
                  className="tabbar_item body_large_r"
                  activeClassName="is-selected"
                  onClick={() => selectTab(TAB_VALUES.DRIVING)}
                  role="tab"
                  aria-selected={selectedTab === TAB_VALUES.DRIVING}
                  aria-controls="tabpanel01"
                  tabIndex={selectedTab === TAB_VALUES.DRIVING ? 0 : -1}
                >
                  <span className="txt">{t('ctn_home_driving_info_tab_eu')}</span>
                </Tab.Item>
                {(isTireVisible || isEvBatteryVisible) && (
                  <Tab.Item
                    element="button"
                    value={TAB_VALUES.VEHICLE}
                    className="tabbar_item body_large_r"
                    activeClassName="is-selected"
                    onClick={() => selectTab(TAB_VALUES.VEHICLE)}
                    role="tab"
                    aria-selected={selectedTab === TAB_VALUES.VEHICLE}
                    aria-controls="tabpanel02"
                    tabIndex={selectedTab === TAB_VALUES.VEHICLE ? 0 : -1}
                  >
                    <span className="txt">{t('ctn_home_vehicle_mntns_tab_eu')}</span>
                  </Tab.Item>
                )}
                {isBenefitVisible && (
                  <Tab.Item
                    element="button"
                    value={TAB_VALUES.BENEFIT}
                    className="tabbar_item body_large_r"
                    activeClassName="is-selected"
                    onClick={() => selectTab(TAB_VALUES.BENEFIT)}
                    role="tab"
                    aria-selected={selectedTab === TAB_VALUES.BENEFIT}
                    aria-controls="tabpanel03"
                    tabIndex={selectedTab === TAB_VALUES.BENEFIT ? 0 : -1}
                  >
                    <span className="txt">{t('ctn_ubi_explore_option_tab_eu')}</span>
                  </Tab.Item>
                )}
              </div>
            </Tab>

            {selectedTab === TAB_VALUES.DRIVING && (
              <div className="tab_content my_trips">
                <DrivingContentsNa vin={vin} />
              </div>
            )}
            {selectedTab === TAB_VALUES.VEHICLE && (
              <div className="tab_content car_management">
                <div className="section">
                  <h2 className="blind">{t('ctn_home_vehicle_mntns_tab_eu')}</h2>
                  {isTireVisible && <HomeTireNa vin={vin} />}
                  {isEvBatteryVisible && <HomeEvBatteryNa vin={vin} />}
                </div>
              </div>
            )}
            {isBenefitVisible && selectedTab === TAB_VALUES.BENEFIT && (
              <div className="tab_content benefits">
                <BenefitContentsNa vin={vin} />
              </div>
            )}
          </>
        ) : (
          <DrivingContentsNa vin={vin} />
        )}
      </main>
    </div>
  );
}

export default HomeContentsNa;
